import {useCallback, useState} from 'react';
import {isBefore, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';

import {BillData} from 'domain/usecases';
import {BillStatusEnum} from 'presentation/constants';
import {useGetOpenObligationListQuery} from '../open-bills/queries/useGetOpenObligationListQuery';
import {OlderBillsActionType} from './types';

export const useOlderBillsCheck = (saoLucasMappedBills: BillData[] = []) => {
	const {data: openBills} = useGetOpenObligationListQuery();

	const [numberOfBills, setNumberOfBills] = useState<number>(0);
	const [mostOldBill, setMostOldBill] = useState<BillData | null>(null);
	const [currentBill, setCurrentBill] = useState<BillData | null>(null);
	const [modalState, setModalState] = useState<OlderBillsActionType | false>(false);

	const isModalOpen = modalState !== false;
	const actionType = modalState;

	const checkOlderBills = useCallback(
		(currentBillParam: BillData, action: OlderBillsActionType = 'details') => {
			if (!openBills) return false;
			const allBills = [...openBills, ...saoLucasMappedBills];
			const allPendingBills = allBills.filter(
				bill =>
					bill.status === BillStatusEnum.EXPIRED ||
					bill.status === BillStatusEnum.IN_NEGOTIATION,
			);

			if (allPendingBills.length === 0) return false;

			const mostOldBill = allPendingBills.reduce((oldest, bill) => {
				const billDateStr = bill.dtCompFim || bill.dtPrevista;
				const oldestDateStr = oldest.dtCompFim || oldest.dtPrevista;

				if (!billDateStr) return oldest;
				if (!oldestDateStr) return bill;

				const billDate = parse(billDateStr, 'dd/MM/yyyy', new Date(), {
					locale: ptBR,
				});
				const oldestDate = parse(oldestDateStr, 'dd/MM/yyyy', new Date(), {
					locale: ptBR,
				});

				return isBefore(billDate, oldestDate) ? bill : oldest;
			}, allPendingBills[0]);

			const isCurrentBillTheOldest = mostOldBill.cdObrigacao === currentBillParam.cdObrigacao;

			if (!isCurrentBillTheOldest) {
				setNumberOfBills(allPendingBills.length);
				setMostOldBill(mostOldBill);
				setCurrentBill(currentBillParam);
				setModalState(action);
				return true;
			}

			return false;
		},
		[openBills, saoLucasMappedBills],
	);

	const handleCloseModal = useCallback(() => {
		setModalState(false);
		setNumberOfBills(0);
		setMostOldBill(null);
		setCurrentBill(null);
	}, []);

	return {
		checkOlderBills,
		numberOfBills,
		mostOldBill,
		currentBill,
		isModalOpen,
		handleCloseModal,
		actionType,
	};
};
